import React, { useEffect, useState } from 'react';
import axios from 'axios';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SECURITY_SETTINGS_API = `${API_BASE_URL}/api/admin-app/security-settings/`;
const SECURITY_SETTINGS_UPDATE_API = `${API_BASE_URL}/api/admin-app/security-settings/update/`;

const AdminSettingsPage = () => {
    const [settings, setSettings] = useState({
        prevent_tab_switch: false,
        detect_window_blur: false,
        prevent_exit: false,
        require_fullscreen: false,
        block_system_keys: false,
        instruction_timer_enabled: true,
        max_violations: 5
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(SECURITY_SETTINGS_API);
                setSettings(response.data);
            } catch (error) {
                console.error('Failed to fetch security settings:', error);
                setMessage({ type: 'danger', text: 'Failed to load settings.' });
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleToggle = (field) => {
        setSettings(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleMaxViolationsChange = (e) => {
        setSettings(prev => ({
            ...prev,
            max_violations: parseInt(e.target.value) || 0
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await axios.post(SECURITY_SETTINGS_UPDATE_API, settings);
            setMessage({ type: 'success', text: 'Settings updated successfully!' });
        } catch (error) {
            console.error('Failed to update security settings:', error);
            setMessage({ type: 'danger', text: 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-panel-card admin-table-section p-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="mb-1">Global Exam Security Settings</h3>
                    <p className="admin-subtitle mb-0">Configure anti-cheating rules applied to all exams across the platform.</p>
                </div>
                <button
                    className="btn btn-primary px-4 fw-bold"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
                </div>
            )}

            <div className="row g-4 mt-2">
                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Prevent Tab Switching</h6>
                                <p className="text-muted mb-0 small">Detects and logs when students switch to other tabs.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.prevent_tab_switch}
                                onChange={() => handleToggle('prevent_tab_switch')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Detect Window Blur</h6>
                                <p className="text-muted mb-0 small">Detects when the exam window loses focus.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.detect_window_blur}
                                onChange={() => handleToggle('detect_window_blur')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Prevent Page Exit</h6>
                                <p className="text-muted mb-0 small">Warns users when they try to leave the page.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.prevent_exit}
                                onChange={() => handleToggle('prevent_exit')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Require Fullscreen</h6>
                                <p className="text-muted mb-0 small">Forces the exam into fullscreen and blocks exit.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.require_fullscreen}
                                onChange={() => handleToggle('require_fullscreen')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Block System Shortcuts</h6>
                                <p className="text-muted mb-0 small">Blocks Alt, Meta, and DevTools keys.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.block_system_keys}
                                onChange={() => handleToggle('block_system_keys')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Instruction Timer</h6>
                                <p className="text-muted mb-0 small">Shows the countdown before students can start the test.</p>
                            </div>
                            <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                role="switch"
                                checked={settings.instruction_timer_enabled}
                                onChange={() => handleToggle('instruction_timer_enabled')}
                                style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="p-3 border rounded bg-light bg-opacity-50 h-100">
                        <div className="d-flex align-items-center justify-content-between p-0">
                            <div>
                                <h6 className="mb-1 fw-bold">Auto-Submit Threshold</h6>
                                <p className="text-muted mb-0 small">Violations before auto-submission.</p>
                            </div>
                            <input
                                type="number"
                                className="form-control"
                                value={settings.max_violations}
                                onChange={handleMaxViolationsChange}
                                style={{ width: '80px' }}
                                min="1"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 p-3 bg-primary bg-opacity-10 border border-primary border-opacity-10 rounded text-primary">
                <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-3 fa-lg"></i>
                    <p className="mb-0 small fw-bold">Note: Camera monitoring and WebRTC streaming are ALWAYS active and cannot be disabled via these settings.</p>
                </div>
            </div>
        </div>
    );
};

export default AdminSettingsPage;
